'use client';

import * as React from 'react';
import clsx from 'clsx';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  FocusableLink,
} from 'ui';

export const dynamic = 'error';

export default function PartnerPage(): JSX.Element {
  return (
    <div className="flex flex-col items-center space-y-24 py-16 text-center xs:py-24">
      <div className="">
        <p className="mb-4 text-sm font-semibold text-brand-700 md:text-md">
          Partner program
        </p>
        <h1 className="mb-6 text-display-lg font-semibold text-gray-900 md:text-display-xl">
          Earn 30% recurring commissions on customers you refer to Quotion
        </h1>
        <p className="mb-12 text-lg font-medium text-gray-600">
          Become a Quotion partner and earn 30% of every paid plan referral for
          the first 12 months. Earn up to{' '}
          <span className="font-semibold text-brand-700">$597</span> on each
          transaction!
        </p>
        <div className="flex justify-center gap-4">
          <Button
            size="lg"
            type="button"
            variant="outline"
            onClick={() => {
              window.open('mailto:qing@quotion.co', '_blank');
            }}
          >
            Contact Us
          </Button>
          <Button
            onClick={() => {
              window.open('https://quotion.affonso.io', '_blank');
            }}
            className="shadow-[0_4px_14px_0_rgb(var(--brand-600)/39%)]! hover:shadow-[0_6px_20px_rgb(var(--brand-600)/23%)]!"
            size="lg"
          >
            Start Earning
          </Button>
        </div>
      </div>
      <section className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        <StepCard
          index={1}
          title="Sign up"
          content={
            <>
              <FocusableLink
                href="https://quotion.affonso.io"
                className="font-semibold underline"
                target="_blank"
              >
                Sign up here
              </FocusableLink>{' '}
              to join the Quotion Partner Program in just a minute.
            </>
          }
        />
        <StepCard
          index={2}
          title="Share your referral link"
          content={
            <>
              Once you sign up, you will get a personal referral link (e.g.{' '}
              <span className="font-semibold text-brand-700">
                quotion.co?atp=rnpuy
              </span>
              ) to invite others to join Quotion. Share it with your followers,
              audiences, friends, and colleagues.
            </>
          }
        />
        <StepCard
          index={3}
          title="Earn recurring commissions"
          content={
            <>
              When your referrals subscribe to a Quotion paid plan, you'll earn
              a <span className="font-semibold text-brand-700">30% </span>
              recurring commission on every transaction for the first{' '}
              <span className="font-semibold text-brand-700">12 months </span>
              they remain a Quotion customer.
            </>
          }
        />
        <StepCard
          index={4}
          title="Track your referrals, commissions and payouts"
          content="Access to a dashboard where you can track your referrals, commissions, and payouts."
        />
      </section>
      <section className="w-full space-y-6">
        <h2 className="text-display-md font-semibold text-gray-900">
          Frequently Asked Questions
        </h2>
        <Accordion className="mx-auto w-full max-w-2xl" type="multiple">
          <AccordionItem value="how-it-works">
            <FaqAccordionTrigger>How does it work?</FaqAccordionTrigger>
            <FaqAccordionContent>
              It's simple! As a Quotion partner, you:
              <ol className="list-inside list-decimal">
                <li>Get a unique referral link</li>
                <li>Share it with your network</li>
                <li>
                  Earn 30% commission for 12 months when someone subscribes to a
                  paid plan
                </li>
              </ol>
              <br />
              <p>
                The best part? Commissions grow if your referrals upgrade their
                plan!
                <br />
                Ready to start? Just{' '}
                <FocusableLink
                  href="https://quotion.affonso.io/"
                  className="font-semibold underline"
                  target="_blank"
                >
                  sign up here
                </FocusableLink>{' '}
                and you're good to go!
              </p>
            </FaqAccordionContent>
          </AccordionItem>
          <AccordionItem value="track">
            <FaqAccordionTrigger>
              How are referrals tracked?
            </FaqAccordionTrigger>
            <FaqAccordionContent>
              Once you're registered as a referral partner, you can create
              custom referral links. These links will cookie visitors to
              <strong> (app.)quotion.co</strong> domain for 60 days. If they
              become a Quotion
              <strong> paid (Lite / Pro / Enterprise)</strong> customer within
              that 60-day period, you'll be credited as the referrer.
              <br />
              All partners have access to a dashboard where you can track your
              referrals and commissions.
            </FaqAccordionContent>
          </AccordionItem>
          <AccordionItem value="referral-number">
            <FaqAccordionTrigger>How much can I earn?</FaqAccordionTrigger>
            <FaqAccordionContent>
              E.g. for each one of your referrals, if he/she becomes a Pro
              monthly customer, you'll earn{' '}
              <span className="font-semibold text-brand-700">
                $29 x 30% = $8.7
              </span>{' '}
              per month, if he/she upgrade to Enterprise annual plan, you'll
              earn{' '}
              <span className="font-semibold text-brand-700">
                $2490 x 30% = $747
              </span>{' '}
              .
              <br />
              The sky's the limit – the more you refer, the more you earn!
            </FaqAccordionContent>
          </AccordionItem>
          <AccordionItem value="payout">
            <FaqAccordionTrigger>How do payouts work?</FaqAccordionTrigger>
            <FaqAccordionContent>
              Commissions are paid out monthly directly to your{' '}
              <strong>PayPal</strong> account.
              <br />
              This month's commission will be paid on the 30th of the following
              month if the payout amount exceeds <strong>$10</strong>.
            </FaqAccordionContent>
          </AccordionItem>
          <AccordionItem value="who">
            <FaqAccordionTrigger>
              Who can apply for the program?
            </FaqAccordionTrigger>
            <FaqAccordionContent>
              The Quotion Partner Program is open to influencers, bloggers,
              writers, Apple Notes enthusiasts, partnership consultants, growth
              hackers, marketing agencies, and anyone who loves Quotion and
              wants to create an additional income stream while helping
              individuals and companies succeed with affiliate marketing.
            </FaqAccordionContent>
          </AccordionItem>
          <AccordionItem value="assets">
            <FaqAccordionTrigger>
              Where can I find assets to help me promote Quotion?
            </FaqAccordionTrigger>
            <FaqAccordionContent>
              The dashboard you signed up for has an assets page where you can
              find assets to help you promote Quotion, e.g. Quotion
              logo/favicon, banner, Apple Notes logo/screenshot.
            </FaqAccordionContent>
          </AccordionItem>
        </Accordion>
      </section>
      <p>
        If you have any questions or need assistance with the program, please
        don't hesitate to{' '}
        <FocusableLink
          href="mailto:qing@quotion.co"
          className="font-semibold underline"
        >
          contact us
        </FocusableLink>
        .
      </p>
    </div>
  );
}

type StepCardProps = {
  index: number;
  title: string;
  content: React.ReactNode;
};
function StepCard({ index, title, content }: StepCardProps) {
  return (
    <div className="flex flex-col rounded-2xl bg-gray-50 p-6 text-start">
      <p className="mb-16 flex size-12 items-center justify-center rounded-lg border bg-white text-xl font-semibold shadow-xs">
        {index}
      </p>
      <h3 className="mb-2 text-xl font-semibold text-gray-900">{title}</h3>
      <p className="text-md font-medium text-gray-600">{content}</p>
    </div>
  );
}

function FaqAccordionTrigger({
  className,
  ...props
}: React.ComponentProps<typeof AccordionTrigger>) {
  return (
    <AccordionTrigger
      {...props}
      className={clsx('py-8 text-lg text-gray-900', className)}
    />
  );
}

function FaqAccordionContent(
  props: React.ComponentProps<typeof AccordionContent>,
) {
  return (
    <AccordionContent
      {...props}
      className={clsx('text-start text-md text-gray-600', props.className)}
    />
  );
}
